/**
 * 鹿児島県の背景ツアー「鹿児島めぐり」コンテンツ。
 *
 * 軸は時間帯。屋久島の朝の苔の森から、奄美の夜の林道へ。
 *
 * 知覧特攻平和会館について。
 *  solemn を立てている。館自身が「特攻戦死された隊員の当時の姿、遺品、記録を後世に残し、
 *  恒久の平和を祈念することが基地住民の責務」として建てた施設であり、
 *  広島の原爆ドーム・長崎の平和公園と同じ扱いにする。
 *  観光消費的な語（絶景・映え・穴場・必見）、感嘆符、驚き型の見出しを使わない。
 *  「散った」「若き命が輝いた」といった美化の語を使わない。
 *  隊員の人数をクイズや数当てに使わない。
 *  知覧武家屋敷庭園群はこれとは別の場所。取り違えない。
 *
 * 桜島について。
 *  噴火警戒レベルは変わるので、数字を豆知識に書かない。
 *  立入禁止区域があること（災害対策基本法にもとづく指定で、罰則がある）と、
 *  出かける前に最新の発表を確かめること、という変わらない事実だけを書く。
 *  「登る」「火口に近づく」とは書かない。
 *
 * そのほか調査で出てきた「書いてはいけないこと」。
 *  - 「東洋のナイアガラ」（曽木の滝）「薩摩富士」（開聞岳）は公式が使っている呼称だが、
 *    今回の10景では使っていない。
 *  - 池田湖の「イッシー」は伝説として紹介されているもの。事実として書かない。
 *  - 白谷雲水峡を特定の映画作品と結びつけない。
 *  - 「日本最大のツルの集団越冬地」は県教育委員会が使っているので書ける。
 *    ただし「世界一」に言い換えない。
 */

import type { StageTour } from '../types';

export const kagoshimaSceneryTour: StageTour = {
  scenes: [
    {
      id: 'shiratani-unsuikyo',
      name: '白谷雲水峡の苔の森',
      area: '屋久島町・朝',
      image: '/assets/scenery/kagoshima/scenes/01_shiratani_unsuikyo.webp',
      hotspots: [
        {
          id: 'shiratani-hyoko',
          x: 0.46,
          y: 0.36,
          title: '標高600mから1050mの森',
          body: '白谷雲水峡は標高600メートルから1,050メートルに広がる森です。歩くコースは3つあり、短いもので1時間ほど、長いもので4時間ほどかかります。増水時にはコースが制限されます。',
          source:
            'https://www.town.yakushima.kagoshima.jp/soshiki/kanko_machizukuri/kankosushin/kankospot/yama/863.html',
          mapQuery: '白谷雲水峡',
        },
        {
          id: 'shiratani-kyoryokukin',
          x: 0.72,
          y: 0.46,
          title: '森に入るときの協力金',
          body: '屋久島レクリエーションの森では森林環境整備推進協力金を集めています。2026年4月から1人800円、20名以上の団体は1人600円に改定されました。',
          source:
            'https://www.town.yakushima.kagoshima.jp/soshiki/kanko_machizukuri/kankosushin/kankospot/yama/863.html',
          mapQuery: '白谷雲水峡',
        },
        {
          id: 'jomonsugi-9h',
          x: 0.22,
          y: 0.48,
          title: '縄文杉は往復9時間以上',
          body: '環境省の案内では、荒川口から縄文杉を往復する日帰りコースはおよそ22キロ、標高差およそ700メートル、所要9時間以上です。体力度はレベル4とされ、十分な体力と登山経験、登山計画が求められます。',
          source: 'https://www.env.go.jp/park/yakushima/ywhcc/route/3-1.html',
          mapQuery: '縄文杉',
        },
      ],
    },
    {
      id: 'izumi-tsuru',
      name: '出水平野のツル',
      area: '出水市・冬の朝',
      image: '/assets/scenery/kagoshima/scenes/02_izumi_tsuru.webp',
      hotspots: [
        {
          id: 'tsuru-tokubetsu',
          x: 0.46,
          y: 0.42,
          title: '国の特別天然記念物',
          body: '「鹿児島県のツルおよびその渡来地」は1952年3月29日に国の特別天然記念物に指定されました。毎年10月中旬ごろから飛来し、翌年3月下旬ごろまでとどまります。',
          source:
            'https://www.pref.kagoshima.jp/bc05/hakubutsukan/tennen/kuni_tokubetu/01crane.html',
          mapQuery: '出水市ツル観察センター',
        },
        {
          id: 'tsuru-nabezuru',
          x: 0.72,
          y: 0.34,
          title: 'ナベヅルが8割',
          body: '出水に渡来するツルはナベヅルがおよそ80パーセント、マナヅルがおよそ20パーセントです。飛来数は1951年以降ふえ続け、1万羽を超えるまでになりました。日本最大のツルの集団越冬地とされています。',
          source:
            'https://www.pref.kagoshima.jp/bc05/hakubutsukan/tennen/kuni_tokubetu/01crane.html',
          mapQuery: '出水平野',
        },
        {
          id: 'tsuru-nyuiki',
          x: 0.22,
          y: 0.36,
          title: '入域料と決められた道',
          body: '見学には入域料の支払いと指定ルートの通行が必要です。まずツル観察センターへ立ち寄ります。羽数調査の日は、午前9時までエリアに立ち入ることができません。',
          source: 'https://www.izumi-navi.jp/news/post-4378/',
          mapQuery: '出水市ツル観察センター',
        },
      ],
    },
    {
      id: 'sakurajima',
      name: '桜島 湯之平展望所',
      area: '鹿児島市・午前',
      image: '/assets/scenery/kagoshima/scenes/03_sakurajima.webp',
      hotspots: [
        {
          id: 'yunohira-373m',
          x: 0.46,
          y: 0.44,
          title: '海抜373mの4合目',
          body: '湯之平展望所は鹿児島市桜島小池町にあり、海抜373メートル、桜島岳の4合目にあたります。眼下に錦江湾と鹿児島市街地が広がり、北に霧島連山、南に開聞岳を望みます。年中無休です。',
          source: 'https://www.city.kagoshima.lg.jp/kanshin/shisetsu/kanko/010.html',
          mapQuery: '湯之平展望所',
        },
        {
          id: 'sakurajima-kinshi',
          x: 0.7,
          y: 0.28,
          title: '火口のまわりは立入禁止',
          body: '鹿児島市は災害対策基本法にもとづき、南岳山頂火口と昭和火口のまわりを警戒区域として立入禁止にしています。違反には罰則があります。山頂へ登ることはできません。',
          source:
            'https://www.city.kagoshima.lg.jp/kikikanri/kurashi/bosai/bosai/sakurajima/kinshi.html',
          mapQuery: '桜島',
        },
        {
          id: 'sakurajima-level',
          x: 0.22,
          y: 0.32,
          title: '規制の範囲は変わる',
          body: 'ここはいまも噴火を続けている火山です。噴火警戒レベルによって立入禁止の範囲が変わります。出かける前に、鹿児島市と気象庁の最新の発表を必ず確かめてください。',
          source: 'https://www.data.jma.go.jp/vois/data/report/activity_info/506.html',
          mapQuery: '桜島',
        },
      ],
    },
    {
      id: 'senganen',
      name: '仙巌園',
      area: '鹿児島市・昼',
      image: '/assets/scenery/kagoshima/scenes/04_senganen.webp',
      hotspots: [
        {
          id: 'senganen-meisho',
          x: 0.44,
          y: 0.5,
          title: '桜島を庭に借りる',
          body: '仙巌園は薩摩藩島津家の別邸としてはじめられた庭園で、国の名勝に指定されています。南の正面に錦江湾をへだてて、桜島の全容を借景としています。',
          source: 'https://online.bunka.go.jp/heritages/detail/206863',
          mapQuery: '仙巌園',
        },
        {
          id: 'shuseikan',
          x: 0.72,
          y: 0.32,
          title: '鉄を溶かすための反射炉',
          body: '世界文化遺産「明治日本の産業革命遺産」の鹿児島市の構成資産は、旧集成館、寺山炭窯跡、関吉の疎水溝の3つです。旧集成館の反射炉は鉄を溶かして大砲を造るためのもので、1857年に建設された2号炉の下部構造が残っています。',
          source:
            'https://www.city.kagoshima.lg.jp/kikakuzaisei/kikaku/sekaiisan/sekaiisann_kagoshimaeria.html',
          mapQuery: '旧集成館',
        },
      ],
    },
    {
      id: 'kirishima-jingu',
      name: '霧島神宮',
      area: '霧島市・昼',
      image: '/assets/scenery/kagoshima/scenes/05_kirishima_jingu.webp',
      hotspots: [
        {
          id: 'kirishima-kokuho',
          x: 0.46,
          y: 0.36,
          title: '2022年に国宝になった社殿',
          body: '霧島神宮の本殿・幣殿・拝殿は2022年2月9日に国宝に指定されました。1989年5月19日に重要文化財に指定されていたものです。',
          source: 'https://online.bunka.go.jp/heritages/detail/148831',
          mapQuery: '霧島神宮',
        },
        {
          id: 'kirishima-gokusaishiki',
          x: 0.72,
          y: 0.46,
          title: '正徳5年の朱と極彩色',
          body: 'いまの社殿は正徳5年、1715年に島津吉貴によって復興されたものです。漆塗りに極彩色と朱塗りで仕上げられ、龍柱、麒麟、唐獅子、奏楽天女などの丸彫り彫刻が施されています。',
          source: 'https://kirishimajingu.or.jp/national-treasure/',
          mapQuery: '霧島神宮',
        },
      ],
    },
    {
      id: 'chiran-buke',
      name: '知覧武家屋敷庭園群',
      area: '南九州市・午後',
      image: '/assets/scenery/kagoshima/scenes/06_chiran_buke.webp',
      hotspots: [
        {
          id: 'chiran-7niwa',
          x: 0.46,
          y: 0.44,
          title: '7つの庭が国の名勝',
          body: '知覧武家屋敷庭園群のうち、一般公開している7つの庭園が国の名勝に指定されています。地区全体は面積18.6ヘクタールの重要伝統的建造物群保存地区でもあります。',
          source:
            'https://www.city.minamikyushu.lg.jp/soshikikarasagasu/shokokankoka/kankokoryu/1/293.html',
          mapQuery: '知覧武家屋敷庭園',
        },
        {
          id: 'chiran-shakkei',
          x: 0.72,
          y: 0.3,
          title: '母ヶ岳を借りた庭',
          body: '庭は背後の母ヶ岳を借景にしてつくられています。曲折のある道路に沿って石垣と生け垣が連なり、市は「母ヶ岳を借景にした箱庭のような通り」と紹介しています。',
          source:
            'https://www.city.minamikyushu.lg.jp/soshikikarasagasu/shokokankoka/kankokoryu/1/293.html',
          mapQuery: '知覧武家屋敷庭園',
        },
      ],
    },
    {
      id: 'chiran-heiwa',
      name: '知覧特攻平和会館',
      area: '南九州市・午後',
      image: '/assets/scenery/kagoshima/scenes/07_chiran_heiwa.webp',
      solemn: true,
      hotspots: [
        {
          id: 'chiran-riyu',
          x: 0.46,
          y: 0.44,
          title: '会館が建てられた理由',
          body: '知覧が出撃基地だったことから、地域の住民が、特攻戦死された隊員の当時の姿、遺品、記録を後世に残し、恒久の平和を祈念することが基地住民の責務であると考えて建設した施設です。',
          source: 'https://www.chiran-tokkou.jp/heiwakaikan.html',
          mapQuery: '知覧特攻平和会館',
        },
        {
          id: 'chiran-kotoba',
          x: 0.26,
          y: 0.36,
          title: '館が掲げている言葉',
          body: '館は「命の尊さ・尊厳を無視した戦法は絶対とってはならない、また、このような悲劇を生み出す戦争も起こしてはならない」という趣旨を掲げ、陸軍特別攻撃隊員の遺品や関係資料を展示しています。',
          source: 'https://www.chiran-tokkou.jp/heiwakaikan.html',
          mapQuery: '知覧特攻平和会館',
        },
        {
          id: 'chiran-kimari',
          x: 0.72,
          y: 0.36,
          title: '館内での決まり',
          body: '開館は9時から17時で、入館は16時30分までです。展示室での撮影は禁止されており、撮影できるのはロビーと零戦展示室のみ。フラッシュ、三脚、自撮り棒は使えません。',
          source: 'https://www.chiran-tokkou.jp/museum.html',
          mapQuery: '知覧特攻平和会館',
        },
      ],
    },
    {
      id: 'kaimondake',
      name: '開聞岳と池田湖',
      area: '指宿市・夕',
      image: '/assets/scenery/kagoshima/scenes/08_kaimondake.webp',
      hotspots: [
        {
          id: 'kaimon-924m',
          x: 0.5,
          y: 0.3,
          title: '標高924mの円すい',
          body: '開聞岳は薩摩半島の南端にある標高924メートルの山です。円すい形の姿から「薩摩富士」とも呼ばれ、霧島錦江湾国立公園に含まれます。',
          source: 'https://www.ibusuki.or.jp/tourism/view/kaimondake/',
          mapQuery: '開聞岳',
        },
        {
          id: 'ikedako',
          x: 0.28,
          y: 0.5,
          title: '周囲15km・水深233m',
          body: '池田湖は周囲15キロ、水深233メートルのカルデラ湖です。湖水は藍色に澄み、開聞岳を望みます。体長2メートル、胴回り50センチの大うなぎが棲息しています。',
          source: 'https://www.ibusuki.or.jp/tourism/view/ikedalake/',
          mapQuery: '池田湖',
        },
      ],
    },
    {
      id: 'nagame-no-hama',
      name: '甑島 長目の浜',
      area: '薩摩川内市・夕暮れ',
      image: '/assets/scenery/kagoshima/scenes/09_nagame_no_hama.webp',
      hotspots: [
        {
          id: 'koshiki-kokutei',
          x: 0.46,
          y: 0.44,
          title: '2015年指定の国定公園',
          body: '甑島国定公園は2015年3月16日に指定されました。陸域5,447ヘクタール、海域25,288ヘクタール。上甑島・中甑島・下甑島と属島群からなります。',
          source:
            'https://www.pref.kagoshima.jp/ad04/kurashi-kankyo/kankyo/sizenkouen/kennai/koshiki.html',
          mapQuery: '長目の浜',
        },
        {
          id: 'nagame-sasu',
          x: 0.72,
          y: 0.34,
          title: '礫でできた4kmの砂州',
          body: '長目の浜は上甑島の北部にある、礫で形成されたおよそ4キロの大規模な砂州です。その背後に潟湖群があり、周囲には海食崖やリアス式海岸が広がります。',
          source:
            'https://www.pref.kagoshima.jp/ad04/kurashi-kankyo/kankyo/sizenkouen/kennai/koshiki.html',
          mapQuery: '長目の浜',
        },
      ],
    },
    {
      id: 'amami-yoru',
      name: '奄美大島の夜の森',
      area: '奄美市・夜',
      image: '/assets/scenery/kagoshima/scenes/10_amami_yoru.webp',
      hotspots: [
        {
          id: 'amami-isan',
          x: 0.46,
          y: 0.4,
          title: '世界自然遺産の島',
          body: '「奄美大島、徳之島、沖縄島北部及び西表島」は、2021年7月に世界自然遺産になりました。',
          source: 'https://kyushu.env.go.jp/okinawa/amami-okinawa/description/index.html',
          mapQuery: '奄美大島',
        },
        {
          id: 'santaro-sen',
          x: 0.72,
          y: 0.5,
          title: '夜の林道は予約制',
          body: '奄美市道三太郎線では、東西それぞれの入口から30分おきに1台ずつというウェブ事前予約制がとられています。時速10キロ以下で走り、生き物を探すライトは車1台につき1本まで。動物には触らない、餌を与えないという決まりがあります。',
          source: 'https://www.pref.kagoshima.jp/ad13/kurashi-kankyo/kankyo/amami/santarou2.html',
          mapQuery: '奄美市道三太郎線',
        },
        {
          id: 'kinsakubaru',
          x: 0.24,
          y: 0.46,
          title: '金作原はガイド同行が必須',
          body: '金作原では、認定エコツアーガイドの同行が必須で、予約が必要です。同じ時間帯に入れる車両は最大10台、1ツアー最大9人と決められています。',
          source: 'https://www.pref.kagoshima.jp/ad13/kurashi-kankyo/kankyo/amami/kinsakubaru.html',
          mapQuery: '金作原',
        },
      ],
    },
  ],
};
